import { createColumnHelper } from '@tanstack/react-table';
import type { SortingState, VisibilityState } from '@tanstack/react-table';
import { Loader2, Users } from 'lucide-react';
import { useState } from 'react';

import { useGetEnrollmentToStudentRatio } from '~entities/dashboard/api/get-enrollment-to-student-ratio';
import {
  type StudentEnrollmentData,
  useGetTop10EnrolledStudents,
} from '~entities/dashboard/api/get-top-10-enrolled-students';
import { useGetTotalEnrollmentsOverTime } from '~entities/dashboard/api/get-total-enrollments-over-time';

import {
  EnrollioTable,
  EnrollioTableSearch,
} from '~shared/components/enrollio-table';
import { DataTableColumnHeader } from '~shared/components/table';
import { Badge } from '~shared/shadcn/ui/badge';
import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '~shared/shadcn/ui/tooltip';

// Create a column helper
const columnHelper = createColumnHelper<StudentEnrollmentData>();

// Define columns
const columns = [
  columnHelper.accessor('studentName', {
    header: (info) => (
      <DataTableColumnHeader
        className='pl-4 text-black'
        column={info.column}
        title='Student'
        disableHiding
      />
    ),
    cell: (info) => (
      <div className='px-4 py-3 text-sm font-medium text-[#000814]'>
        {info.getValue()}
      </div>
    ),
  }),
  columnHelper.accessor('totalClassesEnrolled', {
    header: (info) => (
      <DataTableColumnHeader
        className='pl-4 text-black'
        column={info.column}
        title='Units'
        disableHiding
      />
    ),
    cell: (info) => (
      <div className='px-4 py-3'>
        <Badge className='w-max bg-[#FFF7E6] text-[#F59E0B] hover:bg-[#FFF7E6] hover:text-[#F59E0B]'>
          {info.getValue()} Units
        </Badge>
      </div>
    ),
  }),
  columnHelper.accessor('classNames', {
    header: (info) => (
      <DataTableColumnHeader
        className='pl-4 text-black'
        column={info.column}
        title='Classes'
        disableHiding
        disableSorting
      />
    ),
    cell: (info) => {
      const classes = info.getValue();
      const preview = classes.slice(0, 3).join(', ');
      const extraCount = classes.length - 3;
      return (
        <div className='px-4 py-3 text-sm text-[#6B7280]'>
          <p className='break-words md:hidden'>{classes.join(', ')}</p>
          <div className='hidden md:block'>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className='cursor-default'>
                    {preview}
                    {extraCount > 0 && ` +${extraCount} more`}
                  </span>
                </TooltipTrigger>
                <TooltipContent className='max-w-xs'>
                  <div className='flex flex-col gap-1'>
                    {classes.map((className, index) => (
                      <span key={`${className}-${index}`}>{className}</span>
                    ))}
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      );
    },
  }),
];

interface EnrollmentDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionId: string;
}

export const EnrollmentDetailModal = ({
  open,
  onOpenChange,
  sessionId,
}: EnrollmentDetailModalProps) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  const {
    data: totalEnrollmentsOverTime,
    isLoading: isTotalEnrollmentsOverTimeLoading,
  } = useGetTotalEnrollmentsOverTime({ sessionId });

  const {
    data: top10EnrolledStudents,
    isLoading: isTop10EnrolledStudentsLoading,
  } = useGetTop10EnrolledStudents({ sessionId });

  const {
    data: enrollmentToStudentRatio,
    isLoading: isEnrollmentToStudentRatioLoading,
  } = useGetEnrollmentToStudentRatio({ sessionId });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='flex max-h-[calc(100svh-2rem)] w-[calc(100%-2rem)] flex-col overflow-y-auto overscroll-contain p-4 sm:max-w-4xl sm:p-6'>
        <DialogHeader className='pr-8 text-left'>
          <DialogTitle className='text-xl font-semibold text-[#000814] sm:text-2xl'>
            Enrollment Detail
          </DialogTitle>
          <p className='mt-1 text-sm text-[#6B7280]'>
            Deep dive into student enrollment patterns and multi-class
            participation
          </p>
        </DialogHeader>

        <div className='mt-6 space-y-6'>
          {/* Summary Stats */}
          <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3'>
            <div className='rounded-xl border bg-muted/50 p-4'>
              <p className='mb-1 text-sm text-[#6B7280]'>Total Students</p>
              <p className='text-3xl font-semibold text-[#000814]'>
                {isEnrollmentToStudentRatioLoading ? (
                  <Loader2 className='size-6 animate-spin' />
                ) : (
                  (enrollmentToStudentRatio?.totalActiveStudents ?? 0)
                )}
              </p>
              <p className='mt-0.5 text-xs text-[#6B7280]'>
                Active students in selected session scope
              </p>
            </div>
            <div className='rounded-xl border bg-muted/50 p-4'>
              <p className='mb-1 text-sm text-[#6B7280]'>Total Units</p>
              <p className='text-3xl font-semibold text-[#000814]'>
                {isTotalEnrollmentsOverTimeLoading ? (
                  <Loader2 className='size-6 animate-spin' />
                ) : (
                  (totalEnrollmentsOverTime?.totalEnrollments ?? 0)
                )}
              </p>
              <p className='mt-0.5 text-xs text-[#6B7280]'>
                Total enrollments in selected session (1 student in 3 classes =
                3 units)
              </p>
            </div>
            <div className='rounded-xl border bg-muted/50 p-4'>
              <p className='mb-1 text-sm text-[#6B7280]'>
                Unit-to-Student Ratio
              </p>
              <p className='text-3xl font-semibold text-[#000814]'>
                {isEnrollmentToStudentRatioLoading ? (
                  <Loader2 className='size-6 animate-spin' />
                ) : (
                  Number(enrollmentToStudentRatio?.ratio ?? 0).toFixed(2)
                )}
              </p>
              <p className='mt-0.5 text-xs text-[#6B7280]'>
                Avg units per student in selected session
              </p>
            </div>
          </div>

          {/* Top Multi-Class Students */}
          <div>
            <h3 className='mb-1 flex items-center gap-2 text-lg font-semibold text-[#000814]'>
              <Users className='size-5 text-primary' />
              Top 10 Multi-Class Students
            </h3>
            <p className='mb-3 text-xs text-[#6B7280]'>
              Students with more than 1 class in the selected session, ranked by
              number of classes (rank 1 = most classes)
            </p>
            <EnrollioTable
              ariaLabel='Top multi-class students'
              totalLabel='Multi-class students'
              filterHeader={(table) => (
                <EnrollioTableSearch
                  value={
                    (table.getColumn('studentName')?.getFilterValue() as
                      | string
                      | undefined) ?? ''
                  }
                  onChange={(value) => {
                    table.getColumn('studentName')?.setFilterValue(value);
                  }}
                  placeholder='Search student name...'
                />
              )}
              columns={columns}
              data={top10EnrolledStudents ?? []}
              sorting={sorting}
              onSortingChange={setSorting}
              columnVisibility={columnVisibility}
              onColumnVisibilityChange={setColumnVisibility}
              className='w-full min-w-0'
              isUpdating={isTop10EnrolledStudentsLoading}
              paginationVariant='compact'
            />
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant='outline'>Close</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
